import { Injectable } from '@angular/core';
import { Firestore, collection, query, where, getDocs, DocumentReference, updateDoc, doc, addDoc, getDoc } from '@angular/fire/firestore';
import { UserDocument } from '../interfaces/user-document.interface';
import { ToastrService } from 'ngx-toastr';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DiamondPurchaseModalComponent } from '../components/diamond-purchase-modal/diamond-purchase-modal.component';

@Injectable({
  providedIn: 'root'
})
export class DiamondService {
  constructor(
    private firestore: Firestore,
    private toastr: ToastrService,
    private modalService: NgbModal
  ) {}

  async sendDiamondByUid(
    senderDocRef: DocumentReference, 
    senderDiamonds: number, 
    senderEmail: string, 
    senderId: string,
    receiverUid: string, 
    coins: number
  ): Promise<boolean> {
    try {
      // Kiểm tra số diamonds của người gửi
      if (senderDiamonds < coins) {
        this.toastr.warning('Bạn không đủ 🏵️ để gửi.');
        await this.handleInsufficientDiamonds(senderEmail, senderId);
        return false;
      }

      const confirmSend = window.confirm(`Bạn có chắc chắn muốn thực hiện?`);

      if (!confirmSend) {
        this.toastr.info('Hành động đã bị hủy.');
        return false;
      }

      // Lấy sender ID từ DocumentReference
      const senderUid = senderDocRef.id;

      // Lấy document của người nhận trực tiếp bằng UID
      const receiverDocRef = doc(this.firestore, `User/${receiverUid}`);
      const receiverSnapshot = await getDoc(receiverDocRef);

      if (receiverSnapshot.exists()) {
        const receiverData = receiverSnapshot.data() as UserDocument;

        // Cập nhật số diamonds của người nhận
        let receiverDiamonds = receiverData.diamonds || 0;
        receiverDiamonds += coins;

        // Cập nhật danh sách gifters
        const existingGifterIndex = receiverData.gifters?.findIndex(
          (gifter) => gifter.email === senderEmail
        ) ?? -1;

        const updateData: Partial<UserDocument> = {
          diamonds: receiverDiamonds,
        };

        if (existingGifterIndex === -1) {
          const newGifter = {
            userDocId: senderUid, // Sử dụng ID của người gửi từ DocumentReference
            email: senderEmail,
            lastSent: new Date().toISOString(),
            seen: false,
          };
          updateData.gifters = receiverData.gifters ? [...receiverData.gifters, newGifter] : [newGifter];
        } else if (receiverData.gifters) {
          receiverData.gifters[existingGifterIndex] = {
            ...receiverData.gifters[existingGifterIndex],
            userDocId: senderUid, // Sử dụng ID của người gửi từ DocumentReference
            email: senderEmail,
            lastSent: new Date().toISOString(),
            seen: false,
          };
          updateData.gifters = receiverData.gifters;
        }

        // Cập nhật document của người nhận
        await updateDoc(receiverDocRef, updateData);

        // Trừ diamonds của người gửi
        await updateDoc(senderDocRef, {
          diamonds: senderDiamonds - coins
        });

        return true;
      } else {
        this.toastr.error('Không tìm thấy người nhận!');
        return false;
      }
    } catch (error) {
      console.error('Error sending diamonds by UID:', error);
      return false;
    }
  }

  async spendDiamond(userDocRef: DocumentReference, userData: UserDocument, coins: number) {
    try {
      if (userData.diamonds < coins) {
        this.toastr.warning('Bạn không đủ 🏵️.');
        await this.handleInsufficientDiamonds(userData.email, userData.id, userData.phoneNumber);
        return false;
      }

      const confirmSend = window.confirm(`Bạn có chắc chắn muốn thực hiện?`);

      if (!confirmSend) {
        this.toastr.info('Hành động đã bị hủy.');
        return false;
      }
      await updateDoc(userDocRef, {
        diamonds: userData.diamonds - coins
      });
      return true;
    } catch (error) {
      console.error('Error during diamond spending:', error);
      return false;
    }
  }

  private async handleInsufficientDiamonds(email: string, id: string, phone?: string): Promise<void> {
    const modalRef = this.modalService.open(DiamondPurchaseModalComponent, { size: 'lg' });
    modalRef.componentInstance.phone = phone || '';
  
    try {
      const result = await modalRef.result;
      if (result && result.phone && result.diamondAmount > 0) {
        const purchaseRequest = {
          clientPhone: result.phone,
          clientEmail: email,
          clientUid: id,
          providerEmail: 'pkthienbinh@gmail.com',
          guide: null,
          history: [`Đề nghị mua 🏵️: ${result.diamondAmount}`],
          createdAt: new Date().toISOString(),
          alarm: true,
        };
  
        const collectionRef = collection(this.firestore, 'plans');
        await addDoc(collectionRef, purchaseRequest);
  
        this.toastr.success('Yêu cầu mua 🏵️ đã được gửi. Chúng tôi sẽ liên hệ lại với bạn.', 'Thành công');
      }
    } catch {
      this.toastr.info('Yêu cầu mua kim cương đã bị hủy.', 'Thông báo');
    }
  }
  
}
